import React, { useState, useEffect, useCallback } from 'react';
import ScGrid from '../ScGrid';
import ScModal from '../ScModal';
import ScSpinner from '../ScSpinner';
import { fetchPriorities, createPriority, updatePriority } from '../../studyConfigurationApi';
import { DEFAULT_PRIORITIES } from '../../studyConfigurationDefaults';

const emptyForm = () => ({ priorityDescription: '', hours: 24, isActive: true });

const descKey = (description) => (description ?? '').trim();

const PrioritiesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPriorities();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load priorities.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setModalOpen(true); };
  const openEdit = (row) => {
    setEditId(row.priorityMstNo);
    setForm({ priorityDescription: row.priorityDescription, hours: row.hours, isActive: row.isActive });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await updatePriority(editId, { ...form, priorityMstNo: editId });
      else await createPriority(form);
      setModalOpen(false);
      setSuccess(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  const seedDefaultPriorities = async () => {
    setError(null);
    setSuccess(null);
    const existing = new Set(rows.map((r) => descKey(r.priorityDescription)));
    const missing = DEFAULT_PRIORITIES.filter(
      (p) => !existing.has(descKey(p.priorityDescription))
    );
    const alreadyCount = DEFAULT_PRIORITIES.length - missing.length;

    if (missing.length === 0) {
      setSuccess('All default priorities already exist.');
      return;
    }

    setSeeding(true);
    let inserted = 0;
    let failed = 0;
    let lastError = null;

    for (const priority of missing) {
      try {
        await createPriority({
          priorityDescription: priority.priorityDescription,
          hours: priority.hours,
          isActive: true,
        });
        inserted += 1;
      } catch (e) {
        failed += 1;
        lastError = e.response?.data?.error || 'Insert failed.';
      }
    }

    setSeeding(false);
    await load();

    if (inserted > 0) {
      const parts = [`Inserted ${inserted} priorit${inserted === 1 ? 'y' : 'ies'}.`];
      if (alreadyCount > 0) parts.push(`${alreadyCount} already existed.`);
      if (failed > 0) parts.push(`${failed} failed.`);
      setSuccess(parts.join(' '));
      if (failed > 0 && lastError) setError(lastError);
    } else if (lastError) {
      setError(lastError);
    }
  };

  if (loading) return <ScSpinner />;
  return (
    <div>
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-toolbar">
        <button type="button" className="sc-btn sc-btn-primary" onClick={openAdd} disabled={seeding}>
          Add Priority
        </button>
        <button
          type="button"
          className="sc-btn sc-btn-secondary"
          onClick={seedDefaultPriorities}
          disabled={seeding}
        >
          {seeding ? 'Inserting…' : 'Insert Default Priorities'}
        </button>
      </div>
      <ScGrid
        columns={[
          { key: 'priorityDescription', label: 'Description' },
          { key: 'hours', label: 'SLA Hours' },
          { key: 'isActive', label: 'Active', render: (r) => (r.isActive ? 'Yes' : 'No') },
        ]}
        rows={rows}
        onEdit={openEdit}
      />
      <ScModal
        open={modalOpen}
        title={editId ? 'Edit Priority' : 'Add Priority'}
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button type="button" className="sc-btn sc-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save</button>
          </>
        )}
      >
        <div className="sc-form-row">
          <label>Description</label>
          <input value={form.priorityDescription} onChange={(e) => setForm({ ...form, priorityDescription: e.target.value })} />
        </div>
        <div className="sc-form-row">
          <label>SLA Hours</label>
          <input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} />
        </div>
        <div className="sc-form-row sc-check-item">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} id="pr-active" />
          <label htmlFor="pr-active">Active</label>
        </div>
      </ScModal>
    </div>
  );
};

export default PrioritiesTab;
